<script setup lang="ts">
import { Dialog, Button, InputText, Select } from 'primevue';
import { computed, ref } from 'vue';
import { useLanguageStore } from 'shared/translations/stores/languageStore';
import { storeToRefs } from 'pinia';
import { PackagingLevel } from 'shared/packages/domain/packagingLevel';
import { useCreatePackaging } from '../api/createPackaging';
import { useUpdatePackaging } from '../api/updatePackaging';
import { usePackagingStore } from 'shared/packages/stores/packagingStore';

const emit = defineEmits<{
  saved: [id: string];
}>();

defineExpose({
  show,
  showCopy,
});

const languageStore = useLanguageStore();
const { translations } = storeToRefs(languageStore);
const pageTranslations = computed(() => translations.value.packaging);

const packagingStore = usePackagingStore();
const createPackaging = useCreatePackaging();
const updatePackaging = useUpdatePackaging();

const visible = ref(false);
const loading = ref(false);
const packagingId = ref<string | null>(null);
const name = ref('');
const description = ref('');
const level = ref<PackagingLevel>(PackagingLevel.Primary);

const levelOptions = [
  { label: 'Primary', value: PackagingLevel.Primary },
  { label: 'Secondary', value: PackagingLevel.Secondary },
  { label: 'Tertiary', value: PackagingLevel.Tertiary },
];

const title = computed(() => {
  return packagingId.value ? pageTranslations.value.editPackaging : pageTranslations.value.addNewPackaging;
});

function show(id?: string) {
  if (!id) {
    packagingId.value = null;
    name.value = '';
    description.value = '';
    level.value = PackagingLevel.Primary;
    visible.value = true;
    return;
  }

  const item = packagingStore.getById(id);
  if (!item) {
    return;
  }

  packagingId.value = item.id;
  name.value = item.name;
  description.value = item.description;
  level.value = item.level;
  visible.value = true;
}

function showCopy(id: string) {
  const item = packagingStore.getById(id);
  if (!item) {
    return;
  }

  packagingId.value = null;
  name.value = '';
  description.value = item.description;
  level.value = item.level;
  visible.value = true;
}

async function save() {
  if (!name.value.trim()) return;

  loading.value = true;
  try {
    const payload = {
      name: name.value,
      description: description.value,
      level: level.value,
    };

    const response = packagingId.value
      ? await updatePackaging.execute({ id: packagingId.value, entity: payload })
      : await createPackaging.execute({ entity: payload });

    if (!response.ok) {
      return;
    }

    visible.value = false;
    emit('saved', response.packaging.id);
  } finally {
    loading.value = false;
  }
}
</script>

<template>
  <Dialog v-model:visible="visible" modal :header="title" :style="{ width: '34rem' }">
    <div class="form-grid">
      <label>{{ translations.generic.name }}</label>
      <InputText v-model="name"></InputText>

      <label>{{ translations.generic.description }}</label>
      <InputText v-model="description"></InputText>

      <label>{{ translations.generic.type }}</label>
      <Select v-model="level" :options="levelOptions" optionLabel="label" optionValue="value"></Select>
    </div>

    <template #footer>
      <Button :label="translations.generic.cancel" text @click="visible = false"></Button>
      <Button :label="translations.generic.save" :loading="loading" @click="save"></Button>
    </template>
  </Dialog>
</template>

<style scoped>
.form-grid {
  display: grid;
  grid-template-columns: 10rem 1fr;
  gap: 0.75rem;
  align-items: center;
}
</style>
